import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { useDashboard } from '../../context/DashboardContext';
import { db } from '../../firebase/config';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { IoNotificationsOutline } from 'react-icons/io5';
import { MdCheckBox, MdPeople, MdTrendingUp, MdPerson } from 'react-icons/md';
import { HiOutlineBookOpen, HiOutlineClipboardDocumentCheck, HiOutlineCalendarDays, HiOutlineDocumentText, HiOutlineAcademicCap, HiOutlineSquares2X2 } from 'react-icons/hi2';
import { BsFolder2Open } from 'react-icons/bs';
import { IoStatsChart } from 'react-icons/io5';
import useRole from '../../hooks/useRole';
import Card from '../../components/common/Card';
import Badge from '../../components/common/Badge';
import { friendlyDate } from '../../utils/dateFormat';

const QUICK_MENUS_TEACHER = [
  { key: 'class', label: '클래스', icon: HiOutlineBookOpen, color: 'var(--color-primary)', bg: 'var(--color-primary-light)', path: '/class' },
  { key: 'assignment', label: '과제', icon: HiOutlineClipboardDocumentCheck, color: '#22C55E', bg: '#E8F5E9', path: '/assignment' },
  { key: 'materials', label: '자료실', icon: BsFolder2Open, color: '#42A5F5', bg: '#E3F2FD', path: '/materials' },
  { key: 'grade', label: '시험', icon: IoStatsChart, color: '#FF9800', bg: '#FFF3E0', path: '/grade' },
];
const QUICK_MENUS_STUDENT = [
  { key: 'class', label: '클래스', icon: HiOutlineBookOpen, color: '#22C55E', bg: '#E8F5E9', path: '/class' },
  { key: 'assignment', label: '과제', icon: HiOutlineClipboardDocumentCheck, color: '#22C55E', bg: '#E8F5E9', path: '/assignment' },
  { key: 'materials', label: '수업자료실', icon: BsFolder2Open, color: '#22C55E', bg: '#E8F5E9', path: '/materials' },
  { key: 'grade', label: '시험', icon: IoStatsChart, color: '#22C55E', bg: '#E8F5E9', path: '/grade' },
];
const QUICK_MENUS_PARENT = [
  { key: 'class', label: '클래스', icon: HiOutlineBookOpen, color: '#AB47BC', bg: '#F3E5F5', path: '/class' },
  { key: 'assignment', label: '과제', icon: HiOutlineClipboardDocumentCheck, color: '#AB47BC', bg: '#F3E5F5', path: '/assignment' },
  { key: 'materials', label: '수업자료실', icon: BsFolder2Open, color: '#AB47BC', bg: '#F3E5F5', path: '/materials' },
  { key: 'grade', label: '시험', icon: IoStatsChart, color: '#AB47BC', bg: '#F3E5F5', path: '/grade' },
];

const DAY_MAP = ['일', '월', '화', '수', '목', '금', '토'];
const ATT_LABELS = { present: '출석', late: '지각', absent: '결석', excused: '사유' };
const ATT_COLORS = { present: 'var(--color-primary)', late: '#F59E0B', absent: '#EF4444', excused: '#9CA3AF' };

// 학생 전용: 내 출석 현황 모달 컴포넌트
const StudentAttendanceModal = ({ classes, uid }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const result = [];
      for (const cls of classes) {
        const attSnap = await getDocs(collection(db, 'classes', cls.id, 'attendance'));
        const counts = { present: 0, late: 0, absent: 0, excused: 0 };
        const records = [];
        attSnap.docs.forEach(d => {
          const rec = (d.data().records || {})[uid];
          if (rec) {
            const status = typeof rec === 'string' ? rec : rec.status;
            if (counts[status] !== undefined) counts[status]++;
            records.push({ date: d.data().date || '', status, note: (typeof rec === 'object' ? rec.note : '') || '' });
          }
        });
        records.sort((a, b) => b.date.localeCompare(a.date));
        const total = Object.values(counts).reduce((a, b) => a + b, 0);
        result.push({ className: cls.name, color: cls.color, counts, records, total });
      }
      setData(result);
      setLoading(false);
    })();
  }, [classes, uid]);

  if (loading) return <p style={{ textAlign: 'center', padding: '40px 0', color: '#999', fontSize: 14 }}>불러오는 중...</p>;
  if (!data || data.length === 0) return <p style={{ textAlign: 'center', padding: '40px 0', color: '#999', fontSize: 14 }}>출석 기록이 없습니다.</p>;

  return data.map((cls, ci) => (
    <div key={ci} style={{ background: '#F9FAFB', borderRadius: 10, padding: 14, marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: cls.color || '#9CA3AF', display: 'inline-block' }} />
        <span style={{ fontSize: 14, fontWeight: 700 }}>{cls.className}</span>
        {cls.total > 0 && <span style={{ fontSize: 12, color: '#999', marginLeft: 'auto' }}>총 {cls.total}회</span>}
      </div>
      {cls.total === 0 ? (
        <span style={{ fontSize: 12, color: '#9CA3AF' }}>기록 없음</span>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
            {Object.entries(cls.counts).map(([status, count]) => (
              <span key={status} style={{
                padding: '4px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600,
                background: ATT_COLORS[status] + '18', color: ATT_COLORS[status],
              }}>
                {ATT_LABELS[status]} {count}
              </span>
            ))}
          </div>
          <div style={{ background: 'white', borderRadius: 8, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
            {cls.records.slice(0, 10).map((r, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '8px 12px', fontSize: 12,
                borderBottom: i < Math.min(cls.records.length, 10) - 1 ? '1px solid #F3F4F6' : 'none',
              }}>
                <span style={{ color: '#374151', fontWeight: 500 }}>{friendlyDate(r.date)}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: ATT_COLORS[r.status], fontWeight: 600 }}>{ATT_LABELS[r.status]}</span>
                  {r.note && <span style={{ color: '#9CA3AF', fontSize: 11 }}>{r.note}</span>}
                </div>
              </div>
            ))}
            {cls.records.length > 10 && (
              <div style={{ padding: '8px 12px', fontSize: 11, color: '#9CA3AF', textAlign: 'center' }}>
                +{cls.records.length - 10}건 더 있음
              </div>
            )}
          </div>
        </>
      )}
    </div>
  ));
};

// 학생 전용: 내 시험 성적 모달 컴포넌트
const StudentGradesModal = ({ classes, uid }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const result = [];
      for (const cls of classes) {
        const gSnap = await getDocs(collection(db, 'classes', cls.id, 'grades'));
        const exams = [];
        gSnap.docs.forEach(d => {
          const gData = d.data();
          const myScore = (gData.scores || {})[uid];
          if (myScore !== undefined) {
            // 전체 평균 계산
            const allScores = Object.values(gData.scores || {}).filter(s => typeof s === 'number');
            const avg = allScores.length > 0 ? Math.round(allScores.reduce((a, b) => a + b, 0) / allScores.length) : 0;
            const max = allScores.length > 0 ? Math.max(...allScores) : 0;
            exams.push({
              examName: gData.examName, date: gData.date, maxScore: gData.maxScore || 100,
              myScore, avg, max,
            });
          }
        });
        exams.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
        if (exams.length > 0) {
          result.push({ className: cls.name, color: cls.color, exams });
        }
      }
      setData(result);
      setLoading(false);
    })();
  }, [classes, uid]);

  if (loading) return <p style={{ textAlign: 'center', padding: '40px 0', color: '#999', fontSize: 14 }}>불러오는 중...</p>;
  if (!data || data.length === 0) return <p style={{ textAlign: 'center', padding: '40px 0', color: '#999', fontSize: 14 }}>시험 성적이 없습니다.</p>;

  return data.map((cls, ci) => (
    <div key={ci} style={{ background: '#F9FAFB', borderRadius: 10, padding: 14, marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: cls.color || '#9CA3AF', display: 'inline-block' }} />
        <span style={{ fontSize: 14, fontWeight: 700 }}>{cls.className}</span>
      </div>
      {cls.exams.map((exam, i) => (
        <div key={i} style={{
          background: 'white', borderRadius: 8, border: '1px solid #E5E7EB', padding: 12, marginBottom: 8,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>{exam.examName}</span>
            {exam.date && <span style={{ fontSize: 11, color: '#9CA3AF' }}>{friendlyDate(exam.date)}</span>}
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: 11, color: '#9CA3AF' }}>내 점수</p>
              <p style={{ fontSize: 18, fontWeight: 800, color: 'var(--color-primary)' }}>{exam.myScore}<span style={{ fontSize: 11, color: '#9CA3AF' }}>/{exam.maxScore}</span></p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: 11, color: '#9CA3AF' }}>평균</p>
              <p style={{ fontSize: 18, fontWeight: 800, color: '#374151' }}>{exam.avg}</p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: 11, color: '#9CA3AF' }}>최고</p>
              <p style={{ fontSize: 18, fontWeight: 800, color: '#22C55E' }}>{exam.max}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  ));
};

const HomePage = () => {
  const { userData, currentUser } = useAuth();
  const { isStudent, isTeacher, isParent } = useRole();
  const isChildView = isStudent || isParent; // 학생/학부모: 자녀 데이터 뷰
  const childUid = isParent ? (userData?.children?.[0] || null) : null;
  const targetUid = isParent ? childUid : userData?.uid; // 조회 대상 uid
  const navigate = useNavigate();
  const dashboard = useDashboard();
  const cached = dashboard.isLoaded ? dashboard.data : null;
  const [stats, setStats] = useState(cached ? { attRate: 0, avgScore: 0, ...cached.stats } : { classes: 0, students: 0, ungradedAssignments: 0, events: 0, attRate: 0, avgScore: 0 });
  const [todaySchedule, setTodaySchedule] = useState(cached?.todaySchedule || []);
  const [upcomingAssignments, setUpcomingAssignments] = useState(cached?.upcomingAssignments || []);
  const [upcomingExams, setUpcomingExams] = useState(cached?.upcomingExams || []);
  const [upcomingSupplements, setUpcomingSupplements] = useState(cached?.upcomingSupplements || []);
  const [loading, setLoading] = useState(!cached);

  // 상단 통계 팝업용 데이터
  const [myClasses, setMyClasses] = useState(cached?.myClasses || []);
  const [allAssignments, setAllAssignments] = useState(cached?.allAssignments || []);
  const [allStudents, setAllStudents] = useState(cached?.allStudents || []);
  const [todayEvents, setTodayEvents] = useState(cached?.todayEvents || []);
  const [quickModal, setQuickModal] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentDetail, setStudentDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [expandedAtt, setExpandedAtt] = useState(null);

  // Pull-to-refresh
  const [refreshing, setRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const containerRef = useRef(null);
  const touchStartY = useRef(0);
  const isPulling = useRef(false);

  useEffect(() => {
    if (!userData?.uid) return;
    // 캐시가 없거나, role이 바뀌었으면 새로 로드
    const cachedRole = dashboard.data?.role;
    if (!dashboard.isLoaded || (cachedRole && cachedRole !== userData.role)) {
      loadDashboardData();
    }
    // 10초마다 자동 갱신
    const interval = setInterval(() => {
      loadDashboardData(true);
    }, 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.uid]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadDashboardData();
    setRefreshing(false);
    setPullDistance(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.uid]);

  const handleTouchStart = (e) => {
    const el = containerRef.current;
    if (el && el.scrollTop <= 0) {
      touchStartY.current = e.touches[0].clientY;
      isPulling.current = true;
    }
  };

  const handleTouchMove = (e) => {
    if (!isPulling.current || refreshing) return;
    const diff = e.touches[0].clientY - touchStartY.current;
    if (diff > 0) {
      setPullDistance(Math.min(diff * 0.5, 80));
    }
  };

  const handleTouchEnd = () => {
    if (!isPulling.current) return;
    isPulling.current = false;
    if (pullDistance > 50) {
      handleRefresh();
    } else {
      setPullDistance(0);
    }
  };

  const loadDashboardData = async (silent = false) => {
    try {
      const uid = userData.uid;
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const dayOfWeek = DAY_MAP[today.getDay()];

      // 1. 내 클래스 조회 (강사: teacherId, 학생/학부모: studentIds)
      const lookupUid = isParent ? (userData.children?.[0] || uid) : uid;
      const classQ = isChildView
        ? query(collection(db, 'classes'), where('studentIds', 'array-contains', lookupUid))
        : query(collection(db, 'classes'), where('teacherId', '==', uid));
      const classSnap = await getDocs(classQ);
      const classes = classSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      const totalStudents = new Set(classes.flatMap(c => c.studentIds || [])).size;

      // 2. 오늘 요일에 해당하는 수업 스케줄 (정규 + 보강)
      const scheduleItems = [];
      classes.forEach(cls => {
        (cls.schedule || []).forEach(sch => {
          if (sch.day === dayOfWeek) {
            scheduleItems.push({
              time: sch.startTime || sch.time || '',
              endTime: sch.endTime || '',
              name: cls.name,
              room: sch.room || '',
              type: '정규수업',
              color: cls.color,
            });
          }
        });
        (cls.supplements || []).forEach(sup => {
          if (sup.date === todayStr) {
            scheduleItems.push({
              time: sup.time || '',
              endTime: sup.endTime || '',
              name: cls.name,
              room: '',
              type: '보강수업',
              color: cls.color,
            });
          }
        });
      });
      scheduleItems.sort((a, b) => a.time.localeCompare(b.time));

      // 2-1. 예정된 보강 수업 (오늘 이후, 날짜 지난 건 제외)
      const supList = [];
      classes.forEach(cls => {
        (cls.supplements || []).forEach(sup => {
          if (sup.date > todayStr) {
            supList.push({ date: sup.date, time: sup.time || '', endTime: sup.endTime || '', name: cls.name, color: cls.color });
          }
        });
      });
      supList.sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time));

      // 3. 미제출 과제 수 + 마감 임박 과제
      let unsubmittedCount = 0;
      const assignmentList = [];

      for (const cls of classes) {
        const aSnap = await getDocs(collection(db, 'classes', cls.id, 'assignments'));
        aSnap.docs.forEach(d => {
          const data = { id: d.id, ...d.data(), className: cls.name, classId: cls.id, studentIds: cls.studentIds || [] };
          assignmentList.push(data);
          const submissions = data.submissions || {};
          if (isChildView) {
            // 학생/학부모: 자녀가 제출 안 한 것만 카운트
            if (!submissions[lookupUid] || !submissions[lookupUid].submitted) {
              unsubmittedCount++;
            }
          } else {
            // 강사: 전체 학생 미제출 카운트
            const sids = cls.studentIds || [];
            sids.forEach(sid => {
              if (!submissions[sid] || !submissions[sid].submitted) {
                unsubmittedCount++;
              }
            });
          }
        });
      }

      // 학생/학부모: 출석률 + 평균점수 계산
      let studentAttRate = 0;
      let studentAvgScore = 0;
      if (isChildView) {
        let attTotal = 0, attPresent = 0;
        const scoreList = [];
        for (const cls of classes) {
          // 출석
          const attSnap = await getDocs(collection(db, 'classes', cls.id, 'attendance'));
          attSnap.docs.forEach(d => {
            const records = d.data().records || {};
            const r = records[lookupUid];
            if (r) {
              attTotal++;
              const status = typeof r === 'string' ? r : r.status;
              if (status === 'present' || status === 'late') attPresent++;
            }
          });
          // 성적
          const gSnap = await getDocs(collection(db, 'classes', cls.id, 'grades'));
          gSnap.docs.forEach(d => {
            const scores = d.data().scores || {};
            if (scores[lookupUid] !== undefined) scoreList.push(scores[lookupUid]);
          });
        }
        studentAttRate = attTotal > 0 ? Math.round((attPresent / attTotal) * 100) : 0;
        studentAvgScore = scoreList.length > 0 ? Math.round(scoreList.reduce((a, b) => a + b, 0) / scoreList.length) : 0;
      }

      // 일주일 후 날짜 계산
      const oneWeekLater = new Date(today);
      oneWeekLater.setDate(oneWeekLater.getDate() + 7);
      const oneWeekStr = `${oneWeekLater.getFullYear()}-${String(oneWeekLater.getMonth() + 1).padStart(2, '0')}-${String(oneWeekLater.getDate()).padStart(2, '0')}`;

      // 마감 임박 과제 (오늘~일주일 이내)
      const upcoming = assignmentList
        .filter(a => a.dueDate && a.dueDate >= todayStr && a.dueDate <= oneWeekStr)
        .sort((a, b) => a.dueDate.localeCompare(b.dueDate));

      // 다가오는 시험 (오늘~일주일 이내)
      const examList = [];
      for (const cls of classes) {
        const gSnap = await getDocs(collection(db, 'classes', cls.id, 'grades'));
        gSnap.docs.forEach(d => {
          const data = { id: d.id, ...d.data(), className: cls.name, classId: cls.id };
          if (data.date && data.date >= todayStr && data.date <= oneWeekStr) {
            examList.push(data);
          }
        });
      }
      examList.sort((a, b) => a.date.localeCompare(b.date));

      // 4. 전체 학생 목록 (강사만 - 학생/학부모는 스킵)
      let studentList = [];
      if (!isChildView) {
        const allSids = [...new Set(classes.flatMap(c => c.studentIds || []))];
        const userMap = {};
        for (const sid of allSids) {
          try {
            const userDoc = await getDoc(doc(db, 'users', sid));
            if (userDoc.exists()) {
              userMap[sid] = { id: sid, ...userDoc.data() };
            }
          } catch {}
        }
        const notFound = allSids.filter(sid => !userMap[sid]);
        if (notFound.length > 0) {
          const studentSnap = await getDocs(collection(db, 'users'));
          studentSnap.docs.forEach(d => {
            const data = d.data();
            if (notFound.includes(data.uid) || notFound.includes(d.id)) {
              const key = notFound.includes(d.id) ? d.id : data.uid;
              userMap[key] = { id: d.id, ...data };
            }
          });
        }
        const studentMap = {};
        for (const cls of classes) {
          for (const sid of (cls.studentIds || [])) {
            if (!studentMap[sid]) {
              const u = userMap[sid] || { id: sid, name: sid };
              studentMap[sid] = { ...u, classNames: [], classColor: cls.color };
            }
            studentMap[sid].classNames.push(cls.name);
          }
        }
        studentList = Object.values(studentMap);
      }

      // 5. 오늘 공지사항 (adminMessages 중 오늘 작성된 것)
      const msgSnap = await getDocs(collection(db, 'adminMessages'));
      const todayNotices = msgSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(m => {
          if (!m.createdAt) return false;
          const ts = m.createdAt.seconds ? new Date(m.createdAt.seconds * 1000) : new Date(m.createdAt);
          const dateStr = `${ts.getFullYear()}-${String(ts.getMonth() + 1).padStart(2, '0')}-${String(ts.getDate()).padStart(2, '0')}`;
          return dateStr === todayStr;
        });

      const statsData = isChildView ? {
        classes: classes.length,
        attRate: studentAttRate,
        ungradedAssignments: unsubmittedCount,
        avgScore: studentAvgScore,
      } : {
        classes: classes.length,
        students: totalStudents,
        ungradedAssignments: unsubmittedCount,
        notices: todayNotices.length,
      };
      setMyClasses(classes);
      setAllAssignments(assignmentList);
      setAllStudents(studentList);
      setTodayEvents(todayNotices);
      setStats(statsData);
      setTodaySchedule(scheduleItems);
      setUpcomingAssignments(upcoming);
      setUpcomingExams(examList);
      setUpcomingSupplements(supList);

      // 캐시 저장
      dashboard.save({
        role: userData.role,
        stats: statsData,
        todaySchedule: scheduleItems,
        upcomingAssignments: upcoming,
        upcomingExams: examList,
        upcomingSupplements: supList,
        myClasses: classes,
        allAssignments: assignmentList,
        allStudents: studentList,
        todayEvents: todayNotices,
      });
    } catch (err) {
      console.error('대시보드 데이터 로드 실패:', err);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return '좋은 아침이에요';
    if (hour < 18) return '좋은 오후에요';
    return '좋은 저녁이에요';
  };

  const ATTENDANCE_LABELS = ATT_LABELS;
  const ATTENDANCE_COLORS = ATT_COLORS;

  const loadStudentDetail = async (student) => {
    setSelectedStudent(student);
    setDetailLoading(true);
    setStudentDetail(null);
    setExpandedAtt(null);
    try {
      const studentClasses = myClasses.filter(c => (c.studentIds || []).includes(student.id) || (c.studentIds || []).includes(student.uid));
      const classDetails = [];

      for (const cls of studentClasses) {
        const sid = (cls.studentIds || []).includes(student.id) ? student.id : student.uid;
        const [attSnap, assignSnap, gradeSnap] = await Promise.all([
          getDocs(collection(db, 'classes', cls.id, 'attendance')),
          getDocs(collection(db, 'classes', cls.id, 'assignments')),
          getDocs(collection(db, 'classes', cls.id, 'grades')),
        ]);

        const attCounts = { present: 0, late: 0, absent: 0, excused: 0 };
        const attRecords = []; // date-by-date records
        let attTotal = 0;
        attSnap.docs.forEach(d => {
          const data = d.data();
          const records = data.records || {};
          const r = records[sid];
          if (r) {
            const status = typeof r === 'string' ? r : r.status;
            if (attCounts[status] !== undefined) attCounts[status]++;
            attTotal++;
            attRecords.push({ date: data.date || '', status, note: (typeof r === 'object' ? r.note : '') || '' });
          }
        });
        attRecords.sort((a, b) => b.date.localeCompare(a.date)); // newest first

        let submitted = 0, totalAssign = 0;
        assignSnap.docs.forEach(d => {
          totalAssign++;
          const subs = d.data().submissions || {};
          const sub = subs[sid];
          if (sub?.submitted || sub?.status === 'submitted') submitted++;
        });

        const gradeList = [];
        gradeSnap.docs.forEach(d => {
          const data = d.data();
          const score = (data.scores || {})[sid];
          if (score !== undefined) {
            gradeList.push({ examName: data.examName, score, maxScore: data.maxScore || 100 });
          }
        });

        classDetails.push({
          classId: cls.id, className: cls.name, color: cls.color, schedule: cls.schedule || [],
          attCounts, attRecords, attTotal, submitted, totalAssign, gradeList,
        });
      }

      const totalAtt = classDetails.reduce((a, b) => a + b.attTotal, 0);
      const totalPresent = classDetails.reduce((a, b) => a + b.attCounts.present, 0);
      const totalSubmitted = classDetails.reduce((a, b) => a + b.submitted, 0);
      const totalAssignAll = classDetails.reduce((a, b) => a + b.totalAssign, 0);
      const allScores = classDetails.flatMap(c => c.gradeList.map(g => g.score));
      const avgScore = allScores.length > 0 ? Math.round(allScores.reduce((a, b) => a + b, 0) / allScores.length) : 0;

      setStudentDetail({
        attRate: totalAtt > 0 ? Math.round((totalPresent / totalAtt) * 100) : 0,
        subRate: totalAssignAll > 0 ? Math.round((totalSubmitted / totalAssignAll) * 100) : 0,
        avgScore,
        classDetails,
      });
    } catch (e) {
      console.error('학생 상세 로드 실패:', e);
      setStudentDetail({ error: true });
    }
    setDetailLoading(false);
  };

  const closeQuickModal = () => {
    setQuickModal(null);
    setSelectedStudent(null);
    setStudentDetail(null);
  };

  const roleSuffix = { teacher: ' 강사님', student: ' 학생님', assistant: ' 조교님', parent: ' 학부모님' };
  const displayName = isParent
    ? `${userData?.childrenNames?.[0] || '자녀'} ${roleSuffix.parent}`
    : (userData?.name || '사용자') + (roleSuffix[userData?.role] || '님');

  // 역할별 테마 색상
  const themeColor = isParent ? '#AB47BC' : isStudent ? '#22C55E' : 'var(--color-primary)';
  const themeBg = isParent ? '#F3E5F5' : isStudent ? '#E8F5E9' : 'var(--color-primary-light)';
  const badgeVariant = isParent ? 'subjectPurple' : isStudent ? 'subjectGreen' : 'subject';

  // userData 로드 전에는 빈 화면 (색상 깜빡임 방지)
  if (!userData?.role) {
    return <Container style={{ background: '#F5F7FA' }} />;
  }

  return (
    <Container
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {(pullDistance > 0 || refreshing) && (
        <PullIndicator $distance={pullDistance} $refreshing={refreshing}>
          <PullSpinner $refreshing={refreshing || pullDistance > 50} />
          <span>{refreshing ? '새로고침 중...' : pullDistance > 50 ? '놓으면 새로고침' : '당겨서 새로고침'}</span>
        </PullIndicator>
      )}
      <HeaderSection $role={userData?.role}>
        <HeaderTop>
          <div>
            <Greeting>{getGreeting()}</Greeting>
            <UserName>{displayName}</UserName>
          </div>
          {/* 알림 버튼 - 추후 구현 */}
        </HeaderTop>

        <StatsRow>
          <StatItem onClick={() => setQuickModal('class')}>
            <StatIcon><HiOutlineBookOpen size={20} color="white" /></StatIcon>
            <StatValue>{loading ? '-' : `${stats.classes ?? 0}개`}</StatValue>
            <StatLabel>{isChildView ? (isParent ? '자녀 클래스' : '내 클래스') : '담당 클래스'}</StatLabel>
          </StatItem>
          <StatItem onClick={() => setQuickModal(isChildView ? 'myAttendance' : 'students')}>
            <StatIcon>{isChildView ? <MdCheckBox size={20} color="white" /> : <MdPeople size={20} color="white" />}</StatIcon>
            <StatValue>{loading ? '-' : isChildView ? `${stats.attRate ?? 0}%` : `${stats.students ?? 0}명`}</StatValue>
            <StatLabel>{isChildView ? '출석률' : '전체 학생'}</StatLabel>
          </StatItem>
          <StatItem onClick={() => setQuickModal('assignment')}>
            <StatIcon><MdTrendingUp size={20} color="white" /></StatIcon>
            <StatValue>{loading ? '-' : isChildView ? `${stats.ungradedAssignments ?? 0}개` : `${stats.ungradedAssignments ?? 0}명`}</StatValue>
            <StatLabel>미제출 과제</StatLabel>
          </StatItem>
          <StatItem onClick={() => setQuickModal(isChildView ? 'myGrades' : 'events')}>
            <StatIcon>{isChildView ? <IoStatsChart size={20} color="white" /> : <MdPerson size={20} color="white" />}</StatIcon>
            <StatValue>{loading ? '-' : isChildView ? `${stats.avgScore ?? 0}점` : `${stats.notices ?? 0}건`}</StatValue>
            <StatLabel>{isChildView ? '평균 점수' : '오늘 공지'}</StatLabel>
          </StatItem>
        </StatsRow>
      </HeaderSection>

      <ContentArea>
        <SectionHeader>
          <SectionTitleWrap>
            <SectionIcon $color="#222"><HiOutlineCalendarDays size={22} /></SectionIcon>
            <SectionTitle>오늘의 일정</SectionTitle>
          </SectionTitleWrap>
          <SectionLink $themeColor={themeColor} onClick={() => navigate('/calendar')}>캘린더 보기 &gt;</SectionLink>
        </SectionHeader>
        {todaySchedule.length > 0 ? (
          <ScheduleList>
            {todaySchedule.map((item, i) => (
              <ScheduleItem key={i}>
                <ScheduleTime $color={themeColor}>{item.time}{item.endTime ? ` ~ ${item.endTime}` : ''}</ScheduleTime>
                <ScheduleInfo>
                  <ScheduleName>{item.name}</ScheduleName>
                  <ScheduleTags>
                    <Badge $variant={badgeVariant}>{item.type}</Badge>
                    {item.room && <span>{item.room}</span>}
                  </ScheduleTags>
                </ScheduleInfo>
              </ScheduleItem>
            ))}
          </ScheduleList>
        ) : (
          <EmptyBox>
            {loading ? <MiniSpinner /> : '오늘 예정된 수업이 없습니다.'}
          </EmptyBox>
        )}

        {upcomingSupplements.length > 0 && (
          <>
            <SectionHeader>
              <SectionTitleWrap>
                <SectionIcon $color="#222"><HiOutlineCalendarDays size={22} /></SectionIcon>
                <SectionTitle>예정된 보강 수업</SectionTitle>
              </SectionTitleWrap>
            </SectionHeader>
            <SupplementList>
              {upcomingSupplements.map((item, i) => {
                return (
                  <SupplementBadge key={i} $color={themeColor}>
                    <SupplementDate>{friendlyDate(item.date)}</SupplementDate>
                    <SupplementDetail>{item.time}{item.endTime ? ` ~ ${item.endTime}` : ''} · {item.name} · 보강</SupplementDetail>
                  </SupplementBadge>
                );
              })}
            </SupplementList>
          </>
        )}

        <SectionHeader>
          <SectionTitleWrap>
            <SectionIcon $color="#222"><HiOutlineDocumentText size={22} /></SectionIcon>
            <SectionTitle>다가오는 과제</SectionTitle>
          </SectionTitleWrap>
        </SectionHeader>
        {upcomingAssignments.length > 0 ? (
          <Card $mb="16px">
            {upcomingAssignments.map((item, i) => (
              <AssignmentItem key={item.id} $last={i === upcomingAssignments.length - 1}>
                <AssignmentLeft>
                  <Badge $variant={badgeVariant}>{item.className}</Badge>
                  <AssignmentName>{item.title}</AssignmentName>
                  <AssignmentDeadline>마감: {friendlyDate(item.dueDate)}</AssignmentDeadline>
                </AssignmentLeft>
                {isChildView ? (
                  (item.submissions || {})[targetUid]?.submitted
                    ? <Badge $variant="completed">제출완료</Badge>
                    : <Badge $variant="inProgress">미제출</Badge>
                ) : (
                  <Badge $variant="inProgress">진행중</Badge>
                )}
              </AssignmentItem>
            ))}
          </Card>
        ) : (
          <EmptyBox>
            {loading ? <MiniSpinner /> : '다가오는 과제가 없습니다.'}
          </EmptyBox>
        )}

        <SectionHeader>
          <SectionTitleWrap>
            <SectionIcon $color="#222"><HiOutlineAcademicCap size={22} /></SectionIcon>
            <SectionTitle>다가오는 시험일정</SectionTitle>
          </SectionTitleWrap>
        </SectionHeader>
        {upcomingExams.length > 0 ? (
          <Card $mb="16px">
            {upcomingExams.map((item, i) => (
              <AssignmentItem key={item.id} $last={i === upcomingExams.length - 1}>
                <AssignmentLeft>
                  <Badge $variant={badgeVariant}>{item.className}</Badge>
                  <AssignmentName>{item.examName}</AssignmentName>
                  <AssignmentDeadline>날짜: {friendlyDate(item.date)} · 만점: {item.maxScore || 100}</AssignmentDeadline>
                </AssignmentLeft>
              </AssignmentItem>
            ))}
          </Card>
        ) : (
          <EmptyBox>
            {loading ? <MiniSpinner /> : '다가오는 시험일정이 없습니다.'}
          </EmptyBox>
        )}

        <SectionHeader>
          <SectionTitleWrap>
            <SectionIcon $color="#222"><HiOutlineSquares2X2 size={22} /></SectionIcon>
            <SectionTitle>빠른 메뉴</SectionTitle>
          </SectionTitleWrap>
        </SectionHeader>
        <QuickMenuCard>
          {(isParent ? QUICK_MENUS_PARENT : isStudent ? QUICK_MENUS_STUDENT : QUICK_MENUS_TEACHER).map((menu) => (
            <QuickMenuItem key={menu.key} onClick={() => navigate(menu.path)}>
              <QuickMenuIcon $bg={menu.bg}>
                <menu.icon size={22} color={menu.color} />
              </QuickMenuIcon>
              <QuickMenuLabel>{menu.label}</QuickMenuLabel>
            </QuickMenuItem>
          ))}
        </QuickMenuCard>

        <StoreInfoSection>
          <StoreName>ClassCare</StoreName>
          <StoreLinkRow>
            <StoreLink onClick={() => navigate('/terms')}>이용약관</StoreLink>
            <StoreLink onClick={() => navigate('/privacy')}>개인정보 처리지침</StoreLink>
          </StoreLinkRow>
          <StoreBusinessText>상호명 : 윤수빈 영어 연구소 | 대표자명 : 윤수빈</StoreBusinessText>
          <StoreBusinessText>전자우편주소 : kkan2222@gmail.com</StoreBusinessText>
          <StoreBusinessText>사업자등록번호 : 480-86-03245</StoreBusinessText>
          <StoreBusinessText>주소 : 서울특별시 서초구 사임당로8길 13, 4층</StoreBusinessText>
          <StoreNotice>
            윤수빈 영어 연구소는 교육 서비스 제공자로서, 수업 및 학습 관련 서비스의 품질 향상을 위해 최선을 다하고 있습니다.
          </StoreNotice>
        </StoreInfoSection>
      </ContentArea>

      {quickModal !== null && createPortal(
        <div
          onClick={closeQuickModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.5)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: 'calc(100% - 40px)',
              maxWidth: 440,
              background: 'white',
              borderRadius: 16,
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderBottom: '1px solid #E0E0E0' }}>
              {quickModal === 'students' && selectedStudent ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                  <button
                    onClick={() => { setSelectedStudent(null); setStudentDetail(null); }}
                    style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', padding: '0 4px', color: '#333', flexShrink: 0 }}
                  >←</button>
                  {(selectedStudent.photoURL || selectedStudent.profileImage) ? (
                    <img src={selectedStudent.photoURL || selectedStudent.profileImage} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                  ) : (
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--color-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <MdPerson size={16} color="white" />
                    </div>
                  )}
                  <div style={{ minWidth: 0 }}>
                    <p style={{ fontSize: 16, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {selectedStudent.name || selectedStudent.id}
                      {selectedStudent.school && <span style={{ fontWeight: 400, fontSize: 12, color: '#999', marginLeft: 4 }}>({selectedStudent.school} {selectedStudent.grade})</span>}
                    </p>
                    <p style={{ fontSize: 11, color: '#999', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{(selectedStudent.classNames || []).join(', ')}</p>
                  </div>
                </div>
              ) : (
                <p style={{ fontSize: 18 }}>
                  {quickModal === 'class' && (isParent ? '자녀 클래스' : isStudent ? '내 클래스' : '담당 클래스')}
                  {quickModal === 'students' && '전체 학생'}
                  {quickModal === 'myAttendance' && (isParent ? '자녀 출석 현황' : '내 출석 현황')}
                  {quickModal === 'assignment' && '미제출 과제'}
                  {quickModal === 'myGrades' && (isParent ? '자녀 시험 성적' : '내 시험 성적')}
                  {quickModal === 'events' && '오늘 공지사항'}
                </p>
              )}
              <button
                onClick={closeQuickModal}
                style={{ width: 32, height: 32, borderRadius: '50%', border: '1px solid #333', background: 'white', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}
              >✕</button>
            </div>
            <div style={{ padding: '12px 20px 24px', overflowY: 'auto' }}>
              {quickModal === 'class' && (
                myClasses.length === 0 ? <p style={{ textAlign: 'center', padding: '40px 0', color: '#999', fontSize: 14 }}>클래스가 없습니다.</p> :
                myClasses.map(cls => (
                  <div key={cls.id} onClick={() => { closeQuickModal(); navigate(`/class/${cls.id}`); }} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 0', borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: cls.color || 'var(--color-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <HiOutlineBookOpen size={20} color="white" />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: 15, marginBottom: 2 }}>{cls.name}</p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <p style={{ fontSize: 12, color: '#999' }}>{cls.subject || ''} · {cls.studentIds?.length || 0}명</p>
                        <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 4, background: cls.attendanceMode === 'self' ? '#EDE9FE' : '#EEF2FF', color: cls.attendanceMode === 'self' ? '#7C3AED' : '#4A7CFE' }}>
                          {cls.attendanceMode === 'self' ? '위치 기반' : '강사 체크'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
              {quickModal === 'students' && !selectedStudent && (
                allStudents.length === 0 ? <p style={{ textAlign: 'center', padding: '40px 0', color: '#999', fontSize: 14 }}>학생이 없습니다.</p> :
                allStudents.map((s) => (
                  <div key={s.id} onClick={() => loadStudentDetail(s)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}>
                    {(s.photoURL || s.profileImage) ? (
                      <img src={s.photoURL || s.profileImage} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                    ) : (
                      <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--color-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <MdPerson size={18} color="white" />
                      </div>
                    )}
                    <div>
                      <p style={{ fontSize: 14 }}>{s.name || s.id}{s.phone ? <span style={{ fontSize: 12, color: '#999', marginLeft: 6 }}>{s.phone}</span> : ''}</p>
                      <p style={{ fontSize: 12, color: '#999' }}>{(s.classNames || []).join(', ')}</p>
                    </div>
                  </div>
                ))
              )}
              {quickModal === 'students' && selectedStudent && (
                detailLoading ? (
                  <p style={{ textAlign: 'center', padding: '40px 0', color: '#999', fontSize: 14 }}>데이터를 불러오는 중...</p>
                ) : studentDetail?.error ? (
                  <p style={{ textAlign: 'center', padding: '40px 0', color: '#999', fontSize: 14 }}>데이터를 불러올 수 없습니다.</p>
                ) : studentDetail ? (
                  <div>
                    {/* Summary 3 cards */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 20 }}>
                      <div style={{ background: '#F9FAFB', borderRadius: 10, padding: '14px 8px', textAlign: 'center' }}>
                        <p style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 4 }}>출결률</p>
                        <p style={{ fontSize: 22, fontWeight: 800, color: '#111827' }}>{studentDetail.attRate}%</p>
                      </div>
                      <div style={{ background: '#F9FAFB', borderRadius: 10, padding: '14px 8px', textAlign: 'center' }}>
                        <p style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 4 }}>과제제출률</p>
                        <p style={{ fontSize: 22, fontWeight: 800, color: '#111827' }}>{studentDetail.subRate}%</p>
                      </div>
                      <div style={{ background: '#F9FAFB', borderRadius: 10, padding: '14px 8px', textAlign: 'center' }}>
                        <p style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 4 }}>평균성적</p>
                        <p style={{ fontSize: 22, fontWeight: 800, color: '#111827' }}>{studentDetail.avgScore}점</p>
                      </div>
                    </div>

                    {/* Per-class details */}
                    {studentDetail.classDetails.length === 0 && (
                      <p style={{ textAlign: 'center', padding: '20px 0', color: '#999', fontSize: 14 }}>수강 중인 클래스가 없습니다.</p>
                    )}
                    {studentDetail.classDetails.map(cd => (
                      <div key={cd.classId} style={{ background: '#F9FAFB', borderRadius: 10, padding: 14, marginBottom: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: cd.schedule.length > 0 ? 6 : 10 }}>
                          <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: cd.color || '#9CA3AF' }} />
                          <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>{cd.className}</span>
                        </div>
                        {cd.schedule.length > 0 && (
                          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 10 }}>
                            {cd.schedule.map((s, i) => (
                              <span key={i} style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-primary)', background: 'var(--color-primary-light)', padding: '3px 8px', borderRadius: 5 }}>
                                {s.day} {s.startTime || s.time || ''}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Attendance */}
                        <div style={{ marginBottom: 8 }}>
                          <p style={{ fontSize: 11, fontWeight: 600, color: '#9CA3AF', marginBottom: 4 }}>출결</p>
                          {cd.attTotal === 0 ? (
                            <span style={{ fontSize: 12, color: '#9CA3AF' }}>기록 없음</span>
                          ) : (
                            <>
                              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                {Object.entries(cd.attCounts).map(([status, count]) => {
                                  const key = `${cd.classId}::${status}`;
                                  const isExpanded = expandedAtt === key;
                                  return (
                                    <span key={status} onClick={(e) => { e.stopPropagation(); setExpandedAtt(isExpanded ? null : key); }} style={{
                                      display: 'inline-flex', alignItems: 'center', gap: 3, padding: '4px 10px', borderRadius: 10,
                                      fontSize: 12, fontWeight: 600, cursor: 'pointer', userSelect: 'none',
                                      background: isExpanded ? ATTENDANCE_COLORS[status] : ATTENDANCE_COLORS[status] + '18',
                                      color: isExpanded ? 'white' : ATTENDANCE_COLORS[status],
                                      transition: 'all 0.15s',
                                      border: isExpanded ? `1px solid ${ATTENDANCE_COLORS[status]}` : '1px solid transparent',
                                    }}>
                                      {ATTENDANCE_LABELS[status]} {count} {isExpanded ? '▲' : '▼'}
                                    </span>
                                  );
                                })}
                              </div>
                              {expandedAtt && expandedAtt.startsWith(cd.classId + '::') && (() => {
                                const filterStatus = expandedAtt.split('::')[1];
                                const filtered = cd.attRecords.filter(r => r.status === filterStatus);
                                if (filtered.length === 0) return null;
                                return (
                                  <div style={{ marginTop: 8, background: 'white', borderRadius: 8, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
                                    {filtered.map((r, i) => (
                                      <div key={i} style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        padding: '8px 12px', fontSize: 12,
                                        borderBottom: i < filtered.length - 1 ? '1px solid #F3F4F6' : 'none',
                                      }}>
                                        <span style={{ color: '#374151', fontWeight: 500 }}>{friendlyDate(r.date)}</span>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                          <span style={{ color: ATTENDANCE_COLORS[r.status], fontWeight: 600 }}>{ATTENDANCE_LABELS[r.status]}</span>
                                          {r.note && <span style={{ color: '#9CA3AF', fontSize: 11 }}>{r.note}</span>}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                );
                              })()}
                            </>
                          )}
                        </div>

                        {/* Assignments */}
                        <div style={{ marginBottom: 8 }}>
                          <p style={{ fontSize: 11, fontWeight: 600, color: '#9CA3AF', marginBottom: 4 }}>과제</p>
                          {cd.totalAssign === 0 ? (
                            <span style={{ fontSize: 12, color: '#9CA3AF' }}>과제 없음</span>
                          ) : (
                            <span style={{ fontSize: 13, color: '#374151' }}>
                              제출 {cd.submitted}/{cd.totalAssign} ({cd.totalAssign > 0 ? Math.round((cd.submitted / cd.totalAssign) * 100) : 0}%)
                            </span>
                          )}
                        </div>

                        {/* Grades */}
                        <div>
                          <p style={{ fontSize: 11, fontWeight: 600, color: '#9CA3AF', marginBottom: 4 }}>성적</p>
                          {cd.gradeList.length === 0 ? (
                            <span style={{ fontSize: 12, color: '#9CA3AF' }}>성적 없음</span>
                          ) : (
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              {cd.gradeList.map((g, i) => (
                                <span key={i} style={{ padding: '3px 9px', borderRadius: 8, fontSize: 12, color: '#374151', background: 'white', border: '1px solid #E5E7EB' }}>
                                  {g.examName}: <strong>{g.score}</strong>/{g.maxScore}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null
              )}
              {quickModal === 'assignment' && (
                isChildView ? (
                  // 학생/학부모: 미제출 과제 목록
                  (() => {
                    const myUnsubmitted = allAssignments.filter(a => {
                      const subs = a.submissions || {};
                      return !subs[targetUid] || !subs[targetUid].submitted;
                    });
                    return myUnsubmitted.length === 0 ? <p style={{ textAlign: 'center', padding: '40px 0', color: '#999', fontSize: 14 }}>미제출 과제가 없습니다.</p> :
                    myUnsubmitted.map(a => (
                      <div key={`${a.classId}-${a.id}`} style={{ padding: '14px 0', borderBottom: '1px solid #f0f0f0' }}>
                        <p style={{ fontSize: 15, fontWeight: 600, marginBottom: 2 }}>{a.title}</p>
                        <p style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>{a.className} · 마감: {a.dueDate ? friendlyDate(a.dueDate) : '-'}</p>
                      </div>
                    ));
                  })()
                ) : (
                  // 강사: 어떤 학생이 안 냈는지
                  (() => {
                    const withUnsubmitted = allAssignments.map(a => {
                      const subs = a.submissions || {};
                      const sids = a.studentIds || [];
                      const unsubmitted = sids.filter(sid => !subs[sid] || !subs[sid].submitted);
                      return { ...a, unsubmitted };
                    }).filter(a => a.unsubmitted.length > 0);
                    return withUnsubmitted.length === 0 ? <p style={{ textAlign: 'center', padding: '40px 0', color: '#999', fontSize: 14 }}>미제출 과제가 없습니다.</p> :
                    withUnsubmitted.map(a => (
                      <div key={`${a.classId}-${a.id}`} style={{ padding: '14px 0', borderBottom: '1px solid #f0f0f0' }}>
                        <p style={{ fontSize: 15, fontWeight: 600, marginBottom: 2 }}>{a.title}</p>
                        <p style={{ fontSize: 12, color: '#999', marginBottom: 6 }}>{a.className} · 마감: {a.dueDate ? friendlyDate(a.dueDate) : '-'}</p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {a.unsubmitted.map(sid => {
                            const st = allStudents.find(s => s.id === sid);
                            return <span key={sid} style={{ fontSize: 12, background: '#FEF2F2', color: '#EF4444', padding: '2px 8px', borderRadius: 4 }}>{st?.name || sid}</span>;
                          })}
                        </div>
                      </div>
                    ));
                  })()
                )
              )}
              {quickModal === 'events' && (
                todayEvents.length === 0 ? <p style={{ textAlign: 'center', padding: '40px 0', color: '#999', fontSize: 14 }}>오늘 공지사항이 없습니다.</p> :
                todayEvents.map(ev => (
                  <div key={ev.id} style={{ padding: '14px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <p style={{ fontSize: 15, marginBottom: 2 }}>{ev.title}</p>
                    <p style={{ fontSize: 12, color: '#999' }}>{ev.content || ev.description || ''}</p>
                  </div>
                ))
              )}
              {/* 학생 전용: 내 출석 현황 모달 */}
              {quickModal === 'myAttendance' && (
                <StudentAttendanceModal classes={myClasses} uid={targetUid} />
              )}
              {/* 학생/학부모: 시험 성적 모달 */}
              {quickModal === 'myGrades' && (
                <StudentGradesModal classes={myClasses} uid={targetUid} />
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </Container>
  );
};

export default HomePage;

const Container = styled.div`
  background: ${({ theme }) => theme.colors.background.default};
  min-height: 100vh;
  padding-bottom: 80px;
  overflow-y: auto;
  position: relative;
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const PullIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: ${({ $distance, $refreshing }) => $refreshing ? '16px' : `${Math.min($distance * 0.4, 16)}px`} 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.hint};
  transition: ${({ $refreshing }) => $refreshing ? 'padding 0.2s' : 'none'};
`;

const PullSpinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid #E0E0E0;
  border-top-color: ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: ${({ $refreshing }) => $refreshing ? spin : 'none'} 0.8s linear infinite;
  transform: ${({ $refreshing }) => $refreshing ? 'none' : 'rotate(0deg)'};
`;

const HeaderSection = styled.div`
  background: ${({ theme, $role }) => $role === 'parent' ? theme.colors.parentHeaderGradient : $role === 'student' ? theme.colors.studentHeaderGradient : theme.colors.headerGradient};
  padding: 20px 16px 24px;
  border-radius: 0;
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  color: white;
  margin-bottom: 20px;
`;

const Greeting = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  opacity: 0.9;
`;

const UserName = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xxl};
  font-weight: 700;
  margin-top: 4px;
`;

const NotificationButton = styled.button`
  color: white;
  position: relative;
`;

const StatsRow = styled.div`
  display: flex;
  gap: 8px;
`;

const StatItem = styled.div`
  flex: 1;
  background: rgba(255, 255, 255, 0.2);
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: 12px 8px;
  text-align: center;
  color: white;
  cursor: pointer;
  transition: background 0.2s;
  &:hover, &:active {
    background: rgba(255, 255, 255, 0.35);
  }
`;

const StatIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 6px;
`;

const StatValue = styled.p`
  font-size: ${({ theme }) => theme.fontSize.md};
`;

const StatLabel = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xs};
  opacity: 0.8;
  margin-top: 2px;
`;

const ContentArea = styled.div`
  padding: 16px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  margin-top: 16px;
`;

const SectionTitleWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SectionIcon = styled.div`
  color: ${({ $color }) => $color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 22px;
`;

const SectionTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const SectionLink = styled.button`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: #888;
`;

const ScheduleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
`;

const ScheduleItem = styled(Card)`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 16px;
`;

const ScheduleTime = styled.span`
  font-size: ${({ theme }) => theme.fontSize.md};
  color: ${({ $color }) => $color || 'var(--color-primary)'};
  font-weight: 600;
  min-width: 50px;
`;

const ScheduleInfo = styled.div``;

const ScheduleName = styled.p`
  font-size: ${({ theme }) => theme.fontSize.md};
  margin-bottom: 4px;
`;

const ScheduleTags = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const SupplementList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SupplementBadge = styled.div`
  background: ${({ $color }) => $color || 'var(--color-primary)'};
  color: white;
  border-radius: 12px;
  padding: 12px 16px;
`;

const SupplementDate = styled.p`
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 2px;
`;

const SupplementDetail = styled.p`
  font-size: 12px;
  opacity: 0.85;
`;

const AssignmentItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px 0;
  border-bottom: ${({ $last, theme }) => $last ? 'none' : `1px solid ${theme.colors.divider || '#E0E0E0'}`};
`;

const AssignmentLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const AssignmentName = styled.p`
  font-size: ${({ theme }) => theme.fontSize.md};
`;

const AssignmentDeadline = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.hint};
`;

const EmptyBox = styled.div`
  padding: 32px 24px;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.hint};
  font-size: ${({ theme }) => theme.fontSize.md};
  background: white;
  border-radius: 0;
  margin-bottom: 16px;
`;

const spinAnim = keyframes`
  to { transform: rotate(360deg); }
`;

const MiniSpinner = styled.div`
  width: 24px;
  height: 24px;
  border: 3px solid #E5E7EB;
  border-top-color: #9CA3AF;
  border-radius: 50%;
  animation: ${spinAnim} 0.7s linear infinite;
  margin: 0 auto;
`;


const QuickMenuCard = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadow.small};
  padding: 20px 12px;
  margin-bottom: 16px;
`;

const QuickMenuItem = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const QuickMenuIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: ${({ $bg }) => $bg};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const QuickMenuLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const StoreInfoSection = styled.div`
  background: #F2F3F4;
  padding: 20px;
  margin-top: 24px;
  border-radius: 0;
`;

const StoreName = styled.p`
  font-size: 18px;
  font-weight: 700;
  color: #111;
  margin-bottom: 10px;
`;

const StoreLinkRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
`;

const StoreLink = styled.span`
  font-size: 13px;
  color: #333;
  cursor: pointer;
`;

const StoreBusinessText = styled.p`
  font-size: 12px;
  color: #777;
  line-height: 1.6;
  word-break: keep-all;
`;

const StoreNotice = styled.p`
  font-size: 11px;
  color: #777;
  line-height: 1.5;
  margin-top: 14px;
  word-break: keep-all;
`;

